import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { groups as groupsApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Users, Loader2, ExternalLink, DollarSign, Pin } from 'lucide-react';

// Public cohort page — /groups/{slug}. Shows member businesses grid,
// community posts feed, and a Join / Pay-to-join CTA depending on the fee.
export default function GroupPublic() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [postForm, setPostForm] = useState({ title: '', body: '' });

  const load = async () => {
    try { setData(await groupsApi.public(slug)); }
    catch { setData({ notFound: true }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  // If we redirected back from Stripe with a session_id, verify + book.
  useEffect(() => {
    const sid = params.get('session_id');
    const ok = params.get('membership_ok');
    if (ok && sid && data?.id && user) {
      groupsApi.verifyMembership(data.id, sid).then(() => {
        toast({ title: 'Membership active — welcome to the group!' });
        load();
      }).catch(() => { /* ignore — user may have hit the URL again */ });
    }
    // eslint-disable-next-line
  }, [data?.id, user]);

  if (!data) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;
  if (data.notFound) return <div className="text-gray-400">Group not found.</div>;

  const isMember = user && (data.members || []).some(m => m.userId === user.id && m.status === 'active');
  const isOrganizer = user && data.organizerId === user.id;
  const fee = Number(data.membershipFeeUsd || 0);

  const joinFree = async () => {
    setSaving(true);
    try {
      await groupsApi.join(data.id);
      toast({ title: 'You joined the group' });
      await load();
    } catch (e) { toast({ title: 'Join failed', description: e?.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };
  const joinPaid = async () => {
    setSaving(true);
    try {
      const r = await groupsApi.checkoutMembership(data.id);
      window.location.href = r.url;
    } catch (e) {
      toast({ title: 'Checkout failed', description: e?.response?.data?.detail || 'Try again' });
      setSaving(false);
    }
  };
  const submitPost = async () => {
    if (!postForm.title.trim() || !postForm.body.trim()) return;
    try {
      await groupsApi.post(data.id, { title: postForm.title, body: postForm.body, pinned: false });
      setPostForm({ title: '', body: '' });
      toast({ title: 'Posted' });
      await load();
    } catch (e) { toast({ title: 'Post failed', description: e?.response?.data?.detail || '' }); }
  };

  return (
    <div className="space-y-5" data-testid="group-public-page">
      {/* Header */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        {data.coverImage && <img src={data.coverImage} alt="" className="w-full h-40 object-cover"/>}
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Users size={22}/> {data.name}</h1>
              <div className="text-xs text-gray-500 mt-1">Organized by {data.organizerName || 'PBB member'} · {data.memberCount || 0} member{data.memberCount === 1 ? '' : 's'}</div>
              {data.description && <p className="text-sm text-gray-300 mt-2 max-w-2xl">{data.description}</p>}
            </div>
            <div className="flex gap-2">
              {isOrganizer && (
                <Link to="/groups/mine" data-testid="group-open-organizer"
                      className="text-sm px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800">
                  Manage
                </Link>
              )}
              {!isMember && !isOrganizer && user && (
                fee > 0 ? (
                  <button data-testid="group-join-paid" onClick={joinPaid} disabled={saving}
                          className="pbb-btn-green inline-flex items-center gap-1 text-sm disabled:opacity-60">
                    <DollarSign size={14}/> Join for ${fee.toFixed(0)}/yr
                  </button>
                ) : (
                  <button data-testid="group-join-free" onClick={joinFree} disabled={saving}
                          className="pbb-btn-green inline-flex items-center gap-1 text-sm disabled:opacity-60">
                    Join group
                  </button>
                )
              )}
              {isMember && (
                <span className="text-xs px-2 py-1 rounded-full border border-green-500/40 bg-green-500/10 text-green-300">
                  Active member
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Member businesses grid */}
      <div>
        <h2 className="text-white font-semibold mb-2">Member businesses</h2>
        {data.businesses?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.businesses.map(b => (
              <Link to={`/business/${b.id}`} key={b.id} data-testid={`group-member-biz-${b.id}`}
                    className="bg-gray-900 border border-gray-800 rounded-lg p-4 hover:border-green-500 transition-colors">
                <div className="flex items-start gap-3">
                  {b.logo || b.image ? (
                    <img src={b.logo || b.image} alt="" className="w-12 h-12 rounded object-cover"/>
                  ) : <div className="w-12 h-12 rounded bg-gray-800"/>}
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-medium truncate">{b.name}</div>
                    <div className="text-xs text-gray-500 truncate">{b.category} · {b.city || ''}</div>
                  </div>
                  <ExternalLink size={14} className="text-gray-600"/>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-gray-500 text-sm border border-dashed border-gray-800 rounded-lg p-4 text-center">
            No member businesses yet.
          </div>
        )}
      </div>

      {/* Community posts */}
      <div>
        <h2 className="text-white font-semibold mb-2">Community</h2>
        {(isMember || isOrganizer) && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-3 mb-2 space-y-2">
            <input data-testid="group-post-title" placeholder="Title"
                   value={postForm.title} onChange={e => setPostForm({ ...postForm, title: e.target.value })}
                   className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
            <textarea data-testid="group-post-body" placeholder="Say something…" rows={3}
                      value={postForm.body} onChange={e => setPostForm({ ...postForm, body: e.target.value })}
                      className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
            <button data-testid="group-post-submit" onClick={submitPost}
                    className="pbb-btn-green text-sm">Post</button>
          </div>
        )}
        {data.posts?.length ? data.posts.map(p => (
          <div key={p.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4 mb-2"
               data-testid={`group-post-${p.id}`}>
            <div className="flex items-center gap-2">
              {p.pinned && <Pin size={12} className="text-yellow-400"/>}
              <div className="text-white font-medium">{p.title}</div>
            </div>
            <div className="text-xs text-gray-500 mt-1">{p.authorName} · {new Date(p.createdAt).toLocaleString()}</div>
            <div className="text-sm text-gray-300 mt-2 whitespace-pre-wrap">{p.body}</div>
          </div>
        )) : (
          <div className="text-gray-500 text-sm border border-dashed border-gray-800 rounded-lg p-4 text-center">
            No posts yet. Be the first to say something.
          </div>
        )}
      </div>
    </div>
  );
}
